import { z } from "zod";

import { basicValidator } from "@/lib/validators";

const baseWareHousingSchema = z.object({
  customerId: z.string().uuid({
    message: "Required",
  }),
});

export const createWareHouseItemSchema = z.object({
  itemName: basicValidator(true, "Required"),
  isActionForUpdate: z.boolean().optional(),
  quantity: z.coerce.number().min(1, "Required"),
  description: basicValidator(false),
  images: z
    .array(
      z
        .instanceof(File, {
          message: "Required",
        })
        .or(z.string())
        .optional(),
    )
    .optional(),
});

export const createWareHouseProjectSchema = z.object({
  projectId: z.string().uuid({
    message: "Required",
  }),
  customerUserId: z.string().uuid({
    message: "Required",
  }),
  wareHouseItems: z.array(
    z.object({
      itemId: z.string().uuid({
        message: "Required",
      }),
      quantity: z.coerce.number().min(1, {
        message: "Required",
      }),
    }),
  ),
  remarks: z.string().optional(),
});

export const updateWareHouseItemQuantitySchema = z
  .object({
    quantity: z.coerce.number().min(1, "Required"),
    existingQuantity: z.coerce.number().optional(),
    existingOnStockQuantity: z.coerce.number().optional(),
    action: z.enum(["ADD", "REDUCE"]),
    remarks: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.action === "REDUCE") {
      if (data?.existingQuantity === 0) {
        ctx.addIssue({
          message: "Existing quantity is 0. Please add quantity",
          path: ["quantity"],
          code: "custom",
        });
      } else if (
        data.existingQuantity &&
        data.quantity > data.existingQuantity
      ) {
        ctx.addIssue({
          message: "Quantity should not be greater than existing quantity",
          path: ["quantity"],
          code: "custom",
        });
      } else if (
        data.existingOnStockQuantity &&
        data.quantity > data.existingOnStockQuantity
      ) {
        ctx.addIssue({
          message:
            "Quantity should not be greater than existing on stock quantity",
          path: ["quantity"],
          code: "custom",
        });
      }
    }
  });

export const returnWarehouseItemSchema = z.object({
  wareHouseItems: z.array(
    z
      .object({
        itemId: z.string().uuid(),
        itemName: z.string().optional(),
        returned: z.coerce.number().min(1, {
          message: "Required",
        }),
        existingReturned: z.coerce.number().optional(),
        quantity: z.coerce.number(),
        consumed: z.coerce.number(),
      })
      .superRefine((data, ctx) => {
        if (data?.returned > data?.consumed) {
          ctx.addIssue({
            message:
              "Returned quantity should not be greater than consumed quantity",
            path: ["returned"],
            code: "custom",
          });
        }
        if (data?.returned > data?.quantity) {
          ctx.addIssue({
            message: "Returned quantity should not be greater than quantity",
            path: ["returned"],
            code: "custom",
          });
        }
      }),
  ),
  remarks: z.string().optional(),
});

export const StatusUpdateSchema = z.object({
  status: z.enum(["CLOSED"]),
  remarks: z.string().optional(),
});

export const createWareHousingSchema = baseWareHousingSchema;
export type CreateWareHousingSchema = z.infer<typeof createWareHousingSchema>;
export type TCreateWarehouseItem = z.infer<typeof createWareHouseItemSchema>;
export type TCreateWarehouseProject = z.infer<
  typeof createWareHouseProjectSchema
>;
export type TUpdateWareHouseItemQuantity = z.infer<
  typeof updateWareHouseItemQuantitySchema
>;
export type TReturnWarehouseItem = z.infer<typeof returnWarehouseItemSchema>;
export type T_StatusUpdate = z.infer<typeof StatusUpdateSchema>;
